// macadamia-app/src/macadamia-core/components/AuthorsBox.tsx
import React, { useEffect, useState, useCallback, useMemo, memo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import PlusCircle from '../icons/PlusCircle';
import AuthorPopover from './AuthorPopover';
import { apiClient } from '../../utils/apiClient';

interface SkeletonProps {
  width?: string;
  height?: string;
  circle?: boolean;
  style?: React.CSSProperties;
}

const Skeleton = memo(({
  width = '100%',
  height = '16px',
  circle = false,
  style = {}
}: SkeletonProps) => {
  const defaultStyle = {
    width,
    height,
    borderRadius: circle ? '50%' : '4px',
    margin: '4px 0',
    ...style,
  };

  return (
    <div
      style={defaultStyle}
      className={`skeleton bg-mcdm-third-200 dark:bg-mcdm-primary-100`}
    />
  );
});

Skeleton.displayName = 'Skeleton';

interface AuthorsBoxProps {
  dossierId: string;
  userRole: string | null;
  onOpenSettingsModal: (tab: string) => void;
}

interface Author {
  _id: string;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  email: string;
}

interface AuthorDetails {
  author: Author;
  coAuthors: Author[];
}

interface ProfileImageProps {
  author: Author;
  alt: string;
}

// Composant pour l'image de profil avec gestion du cache AuthContext
const ProfileImage = memo(({ author, alt }: ProfileImageProps) => {
  const { getAuthorProfilePicture } = useAuth();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadImage = async () => {
      if (!author._id) return;

      try {
        setIsLoading(true);
        setHasError(false);

        // Utiliser getAuthorProfilePicture qui gère le cache et l'optimisation
        const url = await getAuthorProfilePicture(author._id, 'thumbnail');

        if (isMounted) {
          setImageUrl(url);
          setIsLoading(false);
        }
      } catch (error) {
        console.error(`Erreur chargement image pour ${author._id}:`, error);
        if (isMounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [author._id, getAuthorProfilePicture]);

  const getInitials = () => {
    return `${author.firstName.charAt(0)}${author.lastName.charAt(0)}`;
  };

  if (isLoading) {
    return (
      <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 mr-2 animate-pulse" />
    );
  }

  if (!imageUrl || hasError) {
    return (
      <div className="w-8 h-8 rounded-full bg-mcdm-third-200 text-white text-xs flex items-center justify-center mr-2">
        {getInitials()}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className="w-8 h-8 rounded-full mr-2 object-cover"
      onError={() => setHasError(true)}
    />
  );
});

ProfileImage.displayName = 'ProfileImage';

const AuthorsBox = memo(({
  dossierId,
  userRole,
  onOpenSettingsModal
}: AuthorsBoxProps) => {
  const { logout } = useAuth();
  const [authorDetails, setAuthorDetails] = useState<AuthorDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [is401Error, setIs401Error] = useState(false);
  const [popoverAuthor, setPopoverAuthor] = useState<Author | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const handleAuthorClick = useCallback((author: Author, event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAuthor(author);
    setPopoverAnchor(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopoverAuthor(null);
    setPopoverAnchor(null);
  }, []);

  const isMountedRef = useRef(true);
  const lastRequestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (lastRequestRef.current) {
        lastRequestRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const fetchAuthorDetails = async () => {
      if (is401Error) {
        return;
      }

      if (lastRequestRef.current) {
        lastRequestRef.current.abort();
      }

      const abortController = new AbortController();
      lastRequestRef.current = abortController;

      try {
        if (!isMountedRef.current) return;

        setIsLoading(true);
        setError(null);

        const response = await apiClient(`/dossiers/${dossierId}/authors`, {
          signal: abortController.signal,
        });

        if (!isMountedRef.current) return;

        if (response.status === 401) {
          setError('Session expirée. Veuillez vous reconnecter.');
          setIs401Error(true);

          setTimeout(() => {
            if (isMountedRef.current) {
              logout();
            }
          }, 2000);

          return;
        }

        if (response.status === 403) {
          setError('Accès non autorisé à ce dossier');
          return;
        }

        if (response.status === 404) {
          setError('Dossier introuvable');
          return;
        }

        if (!response.ok) {
          throw new Error(`Erreur HTTP: ${response.status}`);
        }

        const data = await response.json();

        if (!isMountedRef.current) return;

        setAuthorDetails(data);
        setIs401Error(false);

      } catch (error) {
        if (!isMountedRef.current) return;

        const isAbortError = error instanceof DOMException && error.name === 'AbortError';

        if (!isAbortError) {
          console.error('Erreur lors de la récupération des auteurs:', error);

          if (!is401Error && !error) {
            setError(error instanceof Error ? error.message : 'Une erreur est survenue');
          }
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    if (dossierId && !is401Error) {
      const timeoutId = setTimeout(fetchAuthorDetails, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [dossierId, is401Error, logout]);

  const loadingContent = useMemo(() => (
    <div className="pt-5 pl-5 pr-5">
      <h2 className="text-s font-semibold mb-2">Auteur Principal</h2>
      <div className="flex items-center">
        <Skeleton width="32px" height="32px" circle={true} />
        <Skeleton width="100px" style={{ marginLeft: '8px' }} />
      </div>

      <h2 className="text-s font-semibold mt-3 mb-2">Co-Auteurs</h2>
      <ul>
        {[1, 2].map((_, index) => (
          <li key={index} className='text-sm flex items-center mb-3'>
            <Skeleton width="32px" height="32px" circle={true} />
            <Skeleton width="100px" style={{ marginLeft: '8px' }} />
          </li>
        ))}
      </ul>
    </div>
  ), []);

  if (isLoading) {
    return loadingContent;
  }

  if (error) {
    return (
      <div className="pt-5 pl-5 pr-5">
        <p className={`text-sm ${is401Error ? 'text-orange-500' : 'text-red-500'}`}>
          {error}
        </p>
      </div>
    );
  }

  if (!authorDetails) {
    return (
      <div className="pt-5 pl-5 pr-5">
        <p className="text-sm text-gray-500">Aucune information disponible</p>
      </div>
    );
  }

  return (
    <div className="pt-5 pl-5 pr-5">
      <div className="flex items-center mb-2">
        <h2 className="text-[12px] font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
          Auteur Principal
        </h2>
        {(userRole === 'author' || userRole === 'admin') && (
          <button
            onClick={() => onOpenSettingsModal('Auteurs')}
            aria-label="Ajouter Co-Auteur"
            className="ml-2 hover:opacity-80 transition-opacity duration-200"
          >
            <PlusCircle
              className="text-mcdm-secondary-400 dark:text-mcdm-secondary-400"
              width="20px"
              height="20px"
            />
          </button>
        )}
      </div>

      <button
        className="flex items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
        onClick={(e) => handleAuthorClick(authorDetails.author, e)}
      >
        <ProfileImage
          author={authorDetails.author}
          alt={`${authorDetails.author.firstName} ${authorDetails.author.lastName}`}
        />
        <span className='text-sm text-mcdm-third-500 dark:text-mcdm-primary-100'>
          {authorDetails.author.firstName} {authorDetails.author.lastName}
        </span>
      </button>

      {authorDetails.coAuthors.length > 0 && (
        <>
          <h2 className="text-xs font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100 mt-3 mb-2">
            Co-Auteurs
          </h2>
          <ul>
            {authorDetails.coAuthors.map((coAuthor) => (
              <li
                key={coAuthor._id}
                className='text-sm mb-3 text-mcdm-third-500 dark:text-mcdm-primary-100'
              >
                <button
                  className="flex items-center cursor-pointer hover:opacity-80 transition-opacity duration-200"
                  onClick={(e) => handleAuthorClick(coAuthor, e)}
                >
                  <ProfileImage
                    author={coAuthor}
                    alt={`${coAuthor.firstName} ${coAuthor.lastName}`}
                  />
                  <span>
                    {coAuthor.firstName} {coAuthor.lastName}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {popoverAuthor && popoverAnchor && (
        <AuthorPopover
          author={popoverAuthor}
          anchorEl={popoverAnchor}
          onClose={handleClosePopover}
        />
      )}
    </div>
  );
});

AuthorsBox.displayName = 'AuthorsBox';

export default AuthorsBox;